import { useState } from 'react';
import { Link } from 'react-router-dom';

import { CONTRACT_TYPE_PROPOSALS, type ContractTypeProposal, type DecideContractTypeResponse } from '@clm/shared-types';

import { decideContractType } from '../api/contract-type-proposals';
import { ErrorNotice } from '../components/ErrorNotice';
import { BaseDeviationOffer } from './BaseDeviationOffer';
import {
  basePreempts,
  decisionProblemOf,
  familyWords,
  receiptLine,
  sowNumberRule,
  typeNowWords,
  type DecisionAction,
  type DecisionProblem,
  type DecisionSubject,
} from './contract-type-decision';
import { DECISION_KIND_COPY, DECISION_NOTE_COPY, PAUSE_WARNING_COPY } from './type-proposal-copy';

/**
 * The Admin's type decision — CLM-110 (R-110-17, §3.5, D-110-2). One panel,
 * opened from a Type proposals row or from the Contract record.
 *
 * **Preview, then post.** The panel says what the decision will do before it
 * is sent: the type it sets, every family row it writes (the agreement and
 * each amendment, R-110-15), whether a SOW number is required (into SOW, with
 * an input) or cleared (out of SOW, named), and the lineage notes a decision
 * flags but never changes. The current base pre-empts a retype (R-110-21): the
 * panel names it and links to Base MSA rather than letting the API refuse.
 *
 * **Type pending (CLM-118).** A placeholder has no type to keep: the panel says
 * "Type pending → MSA", never "OTHER → MSA", and Type… asks for the type as
 * Correct… does.
 *
 * **`seen` goes back unchanged.** The post carries the subject's `seen` — the
 * attestation of what the Admin looked at. On `409 stale_view` nothing was
 * written; the panel says so in one sentence and offers Reload.
 *
 * **The receipt** names the type, the decision's audit row and how many rows it
 * wrote, and says the ID did not change (FR-35: keys never change). When the
 * new type is MSA it offers the CLM-112 deviation report — the propose-only
 * spec's "ingest-time prompt, which fires once a type is AI-confirmed".
 */
export function ContractTypeDecisionPanel({
  subject,
  action,
  onDecided,
  onCancel,
  onReload,
}: {
  readonly subject: DecisionSubject;
  /** The row action that opened the panel; Correct… and Type… ask for the type. */
  readonly action: DecisionAction;
  readonly onDecided: (response: DecideContractTypeResponse) => void;
  readonly onCancel: () => void;
  /** Re-read whatever the panel was opened from — after `stale_view` or `family_changed`. */
  readonly onReload: () => void;
}) {
  const [type, setType] = useState<ContractTypeProposal | ''>(action.type ?? '');
  const [sowNumber, setSowNumber] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<DecisionProblem | null>(null);

  const key = subject.contract_key;
  const rule = type === '' ? 'none' : sowNumberRule(subject, type);
  const preempted = type !== '' && basePreempts(subject, type);
  const sowMissing = rule === 'required' && sowNumber.trim() === '';
  const canPost = !saving && type !== '' && !preempted && !sowMissing;
  const family = [key, ...subject.amendment_keys];

  async function post() {
    if (type === '') return;
    setSaving(true);
    setProblem(null);
    try {
      const trimmedNote = note.trim();
      const response = await decideContractType({
        contract_key: key,
        contract_type: type,
        ...(rule === 'required' ? { sow_number: sowNumber.trim() } : {}),
        ...(trimmedNote === '' ? {} : { note: trimmedNote }),
        seen: subject.seen,
      });
      onDecided(response);
    } catch (error) {
      setProblem(decisionProblemOf(error));
    } finally {
      setSaving(false);
    }
  }

  const asks = action.id === 'correct' || action.id === 'type';
  const heading =
    action.id === 'correct'
      ? `Correct the type of ${key}`
      : action.id === 'type'
        ? `Type ${key}`
        : `${action.label} for ${key}`;

  return (
    <div className="vform" role="group" aria-label={heading} data-testid="type-decision">
      <h4>{heading}</h4>

      {asks ? (
        <div className="field">
          <label htmlFor={`type-decision-${key}`}>New type</label>
          <select
            id={`type-decision-${key}`}
            value={type}
            disabled={saving}
            onChange={(event) => {
              setType(event.target.value as ContractTypeProposal | '');
              setProblem(null);
            }}
          >
            <option value="">Choose a type…</option>
            {CONTRACT_TYPE_PROPOSALS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {type === '' ? null : (
        <>
          <p data-testid="type-decision-effect">
            Sets the type to <strong>{type}</strong> and confirms it, on {familyWords(subject.amendment_keys.length)}{' '}
            in one audited decision. The contract ID never changes.
          </p>
          <ul className="mut" aria-label="Rows this decision writes" data-testid="type-decision-family">
            {family.map((member) => (
              <li key={member}>
                <code>{member}</code> · {typeNowWords(subject)} → {type}
                {member === key ? '' : ' (amendment — follows its agreement)'}
              </li>
            ))}
          </ul>
        </>
      )}

      {rule === 'required' ? (
        <div className="field">
          <label htmlFor={`type-decision-sow-${key}`}>SOW number *</label>
          <input
            id={`type-decision-sow-${key}`}
            type="text"
            value={sowNumber}
            disabled={saving}
            required
            aria-required="true"
            maxLength={32}
            placeholder="e.g. AGESOW25264OV"
            onChange={(event) => setSowNumber(event.target.value)}
          />
          <p className="mut">
            A SOW is never stored without its number (D-71-0). The agreement and every amendment take it. Type it
            exactly as it appears on the signed document.
          </p>
        </div>
      ) : null}
      {rule === 'cleared' ? (
        <p className="banner up" role="status" data-testid="type-decision-sow-cleared">
          Only a SOW carries a SOW number, so the number on {familyWords(subject.amendment_keys.length)} is cleared
          {subject.sow_number === null ? '' : <> (the agreement’s is <code>{subject.sow_number}</code>)</>}. The SOW
          reconciliation will then report a listed number as missing.
        </p>
      ) : null}
      {rule === 'unchanged' ? <p className="mut">The SOW number is unchanged.</p> : null}

      {subject.lineage_notes.length === 0 ? null : (
        <ul className="mut" aria-label="Flagged, not changed" data-testid="type-decision-notes">
          {subject.lineage_notes.map((lineage) => (
            <li key={lineage.kind}>
              {DECISION_NOTE_COPY[lineage.kind]}{' '}
              {lineage.contract_keys.map((noted) => (
                <code key={noted}>{noted} </code>
              ))}
            </li>
          ))}
        </ul>
      )}

      {/* CLM-121 (R-121-9) — a correction of a safety sample or a policy-approved type pauses auto-approval: say so first. */}
      {subject.pauses_auto_approval_on !== null && type !== '' && type !== subject.contract_type ? (
        <p className="banner up" role="status" data-testid="type-decision-pauses-auto-approval">
          {PAUSE_WARNING_COPY[subject.pauses_auto_approval_on]}
        </p>
      ) : null}

      {preempted ? (
        <p className="banner bad" role="status" data-testid="type-decision-base">
          <code>{key}</code> is the current base {subject.contract_type}, so its type cannot change until the base is
          replaced. <Link to="/admin/base-msa">Replace it on the Base MSA page</Link> first.
        </p>
      ) : null}

      <div className="field">
        <label htmlFor={`type-decision-note-${key}`}>Note (optional)</label>
        <textarea
          id={`type-decision-note-${key}`}
          value={note}
          disabled={saving}
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      <div className="vacts">
        <button type="button" className="btn pri" disabled={!canPost} onClick={() => void post()}>
          {saving ? 'Saving…' : type === '' ? 'Set the type' : `Set type to ${type}`}
        </button>
        <button type="button" className="btn qt" disabled={saving} onClick={onCancel}>
          Cancel
        </button>
      </div>

      {problem === null ? null : <DecisionProblemNotice problem={problem} onReload={onReload} />}
    </div>
  );
}

/**
 * A refusal the API names is the panel's own sentence, never `ErrorNotice`'s
 * copy for its kind — that copy is written for other surfaces (a `conflict` is
 * verify-field's "Another reviewer settled this field"). Anything else — a 403,
 * a 404 (absent and out of scope are one answer), a 5xx — is `ErrorNotice`.
 */
export function DecisionProblemNotice({
  problem,
  onReload,
}: {
  readonly problem: DecisionProblem;
  readonly onReload: () => void;
}) {
  const { refusal } = problem;
  if (refusal === null) return <ErrorNotice kind={problem.kind} correlation_id={problem.correlation_id} />;
  return (
    <div className={`banner ${refusal.tone}`} role="status" data-testid="type-decision-refusal" data-code={refusal.code}>
      <p>{refusal.body}</p>
      {refusal.reload ? (
        <button type="button" className="btn sm" onClick={onReload}>
          Reload
        </button>
      ) : null}
      {problem.correlation_id === null || problem.correlation_id === '' ? null : (
        <p className="mut">
          Correlation id <code>{problem.correlation_id}</code>
        </p>
      )}
    </div>
  );
}

/**
 * What a decision did, from its response: the one line, the kind, and the
 * notes (flagged, never changed). An MSA root is offered the CLM-112 deviation
 * report — the decided contract is never an amendment
 * (`amendment_follows_agreement`), so the type alone decides.
 */
export function ContractTypeDecisionReceipt({ response }: { readonly response: DecideContractTypeResponse }) {
  return (
    <div className="banner up" role="status" data-testid="type-decision-receipt">
      <p>
        <strong>{receiptLine(response)}</strong>
      </p>
      <p className="mut">
        <code>{response.contract_key}</code> — {DECISION_KIND_COPY[response.decision.kind]}
        {response.decision.counted_for_accuracy ? '; counted on the Extraction accuracy page.' : '.'}
      </p>
      {response.notes.length === 0 ? null : (
        <ul className="mut" aria-label="Flagged, not changed">
          {response.notes.map((entry) => (
            <li key={entry.kind}>
              {DECISION_NOTE_COPY[entry.kind]}{' '}
              {entry.contract_keys.map((noted) => (
                <code key={noted}>{noted} </code>
              ))}
            </li>
          ))}
        </ul>
      )}
      <BaseDeviationOffer contractKey={response.contract_key} contractType={response.contract_type} isAmendment={false} />
    </div>
  );
}
